import React, { Fragment } from 'react';
import type {
  INestedDatabaseConfig,
  INestedSchemaConfig,
  IDatabaseConfig,
  ISchemaConfig,
  ITableConfig,
  IViewConfig,
  IColumnConfig,
  IProcedureConfig,
  IFunctionConfig,
  IFileFormatConfig,
  ISequenceConfig,
  IObjectType,
} from '../utils/configEditorTypes';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from 'utils/styleUtils';
import SnowflakeIcon from 'components/SnowflakeIcon';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
} from '@headlessui/react';
import { AdjustmentsHorizontalIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { IEditorModalConfig } from './ConfigEditorModal';

export function DatabaseConfigItem(props: {
  database: INestedDatabaseConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <Disclosure defaultOpen={true}>
      {({ open }) => (
        <>
          <DisclosureButton as="div" className="w-full text-left">
            <div className="w-full relative flex py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group">
              <div className="w-5 mr-1 flex items-center justify-center">
                <ChevronRightIcon
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-4 h-4 text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </div>
              <div className="min-w-[20px] w-5 mr-1">
                <SnowflakeIcon objectType="database" highlighted={false} />
              </div>
              <div className="flex-1 flex items-center justify-between">
                <div className="text-sm truncate font-medium">{props.database.name}</div>
              </div>
              <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
                <EditMenu
                  objectType="database"
                  openEditorModal={props.openEditorModal}
                  setEditorModalConfig={props.setEditorModalConfig}
                  configOnEdit={{
                    action: 'edit',
                    objectType: 'database',
                    objectData: props.database,
                  }}
                  configOnAdd={{
                    action: 'add',
                    objectType: 'schema',
                    objectData: {
                      database: props.database.name,
                      name: '',
                      is_managed: false,
                      is_transient: false,
                      manage_mode: 'none',
                      grants: {},
                    },
                  }}
                  onDeleteObject={props.onDeleteObject}
                />
              </div>
            </div>
          </DisclosureButton>

          <Transition
            as={Fragment}
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel className="space-y-1 pl-5 border-l border-gray-300 ml-2">
              {props.database.schemas.map((schema) => (
                <SchemaConfigItem
                  key={schema.name}
                  schema={schema}
                  setEditorModalConfig={props.setEditorModalConfig}
                  openEditorModal={props.openEditorModal}
                  onDeleteObject={props.onDeleteObject}
                />
              ))}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}

export function SchemaConfigItem(props: {
  schema: INestedSchemaConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <Disclosure defaultOpen={true}>
      {({ open }) => (
        <>
          <DisclosureButton as="div" className="w-full text-left">
            <div className="w-full relative flex py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group">
              <div className="w-5 mr-1 flex items-center justify-center">
                <ChevronRightIcon
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-4 h-4 text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </div>
              <div className="min-w-[20px] w-5 mr-1">
                <SnowflakeIcon objectType="schema" highlighted={false} />
              </div>
              <div className="flex-1 flex items-center justify-between">
                <div className="text-sm truncate">{props.schema.name}</div>
              </div>
              <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
                <EditMenu
                  objectType="schema"
                  openEditorModal={props.openEditorModal}
                  setEditorModalConfig={props.setEditorModalConfig}
                  configOnEdit={{
                    action: 'edit',
                    objectType: 'schema',
                    objectData: props.schema,
                  }}
                  configOnAdd={{
                    action: 'add',
                    objectType: undefined,
                    objectData: {
                      database: props.schema.database,
                      schema: props.schema.name,
                    },
                  }}
                  onDeleteObject={props.onDeleteObject}
                />
              </div>
            </div>
          </DisclosureButton>

          <Transition
            as={Fragment}
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel className="space-y-1 pl-5 border-l border-gray-300 ml-2">
              {/* Tables Group */}
              {props.schema.tables.length > 0 && (
                <ObjectTypeGroup
                  title="Tables"
                  objectType="table"
                  items={props.schema.tables}
                  renderItem={(table) => (
                    <TableConfigItem
                      key={table.name}
                      table={table}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}

              {/* Views Group */}
              {props.schema.views.length > 0 && (
                <ObjectTypeGroup
                  title="Views"
                  objectType="view"
                  items={props.schema.views}
                  renderItem={(view) => (
                    <ViewConfigItem
                      key={view.name}
                      view={view}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}

              {/* Procedures Group */}
              {props.schema.procedures.length > 0 && (
                <ObjectTypeGroup
                  title="Procedures"
                  objectType="procedure"
                  items={props.schema.procedures}
                  renderItem={(procedure) => (
                    <ProcedureConfigItem
                      key={procedure.name}
                      procedure={procedure}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}

              {/* Functions Group */}
              {props.schema.functions.length > 0 && (
                <ObjectTypeGroup
                  title="Functions"
                  objectType="function"
                  items={props.schema.functions}
                  renderItem={(func) => (
                    <FunctionConfigItem
                      key={func.name}
                      function={func}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}

              {/* File Formats Group */}
              {props.schema.file_formats.length > 0 && (
                <ObjectTypeGroup
                  title="File Formats"
                  objectType="file_format"
                  items={props.schema.file_formats}
                  renderItem={(format) => (
                    <FileFormatConfigItem
                      key={format.name}
                      fileFormat={format}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}

              {/* Sequences Group */}
              {props.schema.sequences.length > 0 && (
                <ObjectTypeGroup
                  title="Sequences"
                  objectType="sequence"
                  items={props.schema.sequences}
                  renderItem={(sequence) => (
                    <SequenceConfigItem
                      key={sequence.name}
                      sequence={sequence}
                      setEditorModalConfig={props.setEditorModalConfig}
                      openEditorModal={props.openEditorModal}
                      onDeleteObject={props.onDeleteObject}
                    />
                  )}
                />
              )}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}

export function TableConfigItem(props: {
  table: ITableConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <Disclosure defaultOpen={false}>
      {({ open }) => (
        <>
          <DisclosureButton as="div" className="w-full text-left">
            <div className="w-full relative flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group">
              <div className="w-5 mr-1 flex items-center justify-center">
                <ChevronRightIcon
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-4 h-4 text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </div>
              <div className="min-w-[20px] w-5 mr-1">
                <SnowflakeIcon objectType="table" highlighted={false} />
              </div>
              <div className="text-sm truncate">{props.table.name}</div>
              <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
                <EditMenu
                  objectType="table"
                  openEditorModal={props.openEditorModal}
                  setEditorModalConfig={props.setEditorModalConfig}
                  configOnEdit={{
                    action: 'edit',
                    objectType: 'table',
                    objectData: props.table,
                  }}
                  configOnAdd={{
                    action: 'add',
                    objectType: 'column',
                    objectData: {
                      name: '',
                      schema: props.table.schema,
                      database: props.table.database,
                      table: props.table.name,
                      type: '',
                      nullable: false,
                    },
                  }}
                  onDeleteObject={props.onDeleteObject}
                />
              </div>
            </div>
          </DisclosureButton>

          <Transition
            as={Fragment}
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel className="space-y-1 pl-5 border-l border-gray-300 ml-2">
              {props.table.columns.map((column) => (
                <ColumnConfigItem
                  key={column.name}
                  column={column}
                  setEditorModalConfig={props.setEditorModalConfig}
                  openEditorModal={props.openEditorModal}
                  onDeleteObject={props.onDeleteObject}
                />
              ))}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}

export function ViewConfigItem(props: {
  view: IViewConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="view" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.view.name}</div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="view"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnEdit={{
            action: 'edit',
            objectType: 'view',
            objectData: props.view,
          }}
          configOnAdd={{
            action: 'add',
            objectType: 'view',
            objectData: props.view,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

export function ColumnConfigItem(props: {
  column: IColumnConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="column" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.column.name}</div>
      <div className="ml-2 text-gray-500 text-xs">
        {props.column.type}
        {props.column.nullable ? '' : ' • not null'}
      </div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="column"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnAdd={{
            action: 'edit',
            objectType: 'column',
            objectData: props.column,
          }}
          configOnEdit={{
            action: 'edit',
            objectType: 'column',
            objectData: props.column,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

export function ProcedureConfigItem(props: {
  procedure: IProcedureConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="procedure" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.procedure.name}</div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="procedure"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnEdit={{
            action: 'edit',
            objectType: 'procedure',
            objectData: props.procedure,
          }}
          configOnAdd={{
            action: 'add',
            objectType: 'procedure',
            objectData: props.procedure,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

export function FunctionConfigItem(props: {
  function: IFunctionConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="function" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.function.name}</div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="function"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnEdit={{
            action: 'edit',
            objectType: 'function',
            objectData: props.function,
          }}
          configOnAdd={{
            action: 'add',
            objectType: 'function',
            objectData: props.function,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

export function FileFormatConfigItem(props: {
  fileFormat: IFileFormatConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="file_format" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.fileFormat.name}</div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="file_format"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnEdit={{
            action: 'edit',
            objectType: 'file_format',
            objectData: props.fileFormat,
          }}
          configOnAdd={{
            action: 'add',
            objectType: 'file_format',
            objectData: props.fileFormat,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

export function SequenceConfigItem(props: {
  sequence: ISequenceConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full flex items-center py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group relative">
      <div className="w-5 mr-1 flex items-center justify-center">
        {/* Empty space to align with parent nodes that have arrows */}
      </div>
      <div className="min-w-[20px] w-5 mr-1">
        <SnowflakeIcon objectType="sequence" highlighted={false} />
      </div>
      <div className="text-sm truncate">{props.sequence.name}</div>
      <div className="ml-auto mr-2 h-5 opacity-0 group-hover:opacity-100">
        <EditMenu
          objectType="sequence"
          openEditorModal={props.openEditorModal}
          setEditorModalConfig={props.setEditorModalConfig}
          configOnEdit={{
            action: 'edit',
            objectType: 'sequence',
            objectData: props.sequence,
          }}
          configOnAdd={{
            action: 'add',
            objectType: 'sequence',
            objectData: props.sequence,
          }}
          onDeleteObject={props.onDeleteObject}
        />
      </div>
    </div>
  );
}

interface EditMenuProps {
  objectType: IObjectType;
  configOnEdit: IEditorModalConfig;
  configOnAdd: IEditorModalConfig;
  setEditorModalConfig: (config: IEditorModalConfig) => void;
  openEditorModal: () => void;
  onDeleteObject: (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
}

function EditMenu(props: EditMenuProps): JSX.Element {
  return (
    <Menu as="div" className="inline-block text-left">
      <MenuButton className="flex items-center">
        <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
      </MenuButton>
      <MenuItems
        transition
        className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
      >
        <div className="py-1">
          <MenuItem>
            <button
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 data-focus:bg-gray-100"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                props.setEditorModalConfig(props.configOnEdit);
                props.openEditorModal();
              }}
            >
              <PencilIcon className="mr-3 h-5 w-5" aria-hidden="true" />
              Edit
            </button>
          </MenuItem>
          {(props.objectType === 'database' || props.objectType === 'schema' || props.objectType === 'table') && (
            <MenuItem>
              <button
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 data-focus:bg-gray-100"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  props.setEditorModalConfig(props.configOnAdd);
                  props.openEditorModal();
                }}
              >
                <PlusIcon className="mr-3 h-5 w-5" aria-hidden="true" />
                {props.objectType === 'database'
                  ? 'Add Schema'
                  : props.objectType === 'schema'
                  ? 'Add Object'
                  : props.objectType === 'table'
                  ? 'Add Column'
                  : 'Add'}
              </button>
            </MenuItem>
          )}
          <MenuItem>
            <button
              className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 data-focus:bg-gray-100"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                props.onDeleteObject(
                  props.objectType,
                  props.configOnEdit.objectData as
                    | IDatabaseConfig
                    | ISchemaConfig
                    | ITableConfig
                    | IViewConfig
                    | IProcedureConfig
                    | IFunctionConfig
                    | IFileFormatConfig
                    | ISequenceConfig
                    | IColumnConfig,
                );
              }}
            >
              <TrashIcon className="mr-3 h-5 w-5 text-red-600" aria-hidden="true" />
              Delete
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
}

interface ObjectTypeGroupProps<T> {
  title: string;
  objectType: IObjectType;
  items: T[];
  renderItem: (item: T) => JSX.Element;
}

function ObjectTypeGroup<T>({ title, objectType, items, renderItem }: ObjectTypeGroupProps<T>): JSX.Element {
  return (
    <Disclosure defaultOpen={false}>
      {({ open }) => (
        <>
          <DisclosureButton as="div" className="w-full text-left">
            <div className="w-full relative flex py-1 text-gray-700 cursor-pointer hover:bg-gray-100 rounded group">
              <div className="w-5 mr-1 flex items-center justify-center">
                <ChevronRightIcon
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-4 h-4 text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </div>
              <div className="min-w-[20px] w-5 mr-1">
                <SnowflakeIcon objectType={objectType} highlighted={false} />
              </div>
              <div className="text-sm truncate font-medium">{title}</div>
            </div>
          </DisclosureButton>

          <Transition
            as={Fragment}
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel className="space-y-1 pl-5 border-l border-gray-300 ml-2">
              {items.map(renderItem)}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
